/**
 * One lock over every write to a ranking, shared by the two modules that perform them: `edit-mode.js`
 * (place, remove, move) and `ordered-reorder.js` (drag). Two private flags could not see each other, so a
 * drag could start while a stored operation was still re-reading the screen - the re-read would then put
 * back the pre-drag order while the drag saved on top of it, leaving the database ahead of the page.
 *
 * A `data-` attribute rather than module state: it is the editing surface's own condition, and it stays
 * readable from anywhere that already has the document, including CSS if that is ever wanted.
 *
 * Check and take it in the same synchronous step - `if (isRankingBusy(root)) return; setRankingBusy(root,
 * true);` with no `await` between - or the guard does not hold.
 *
 * The lock has one terminal state, `markRankingStale()`: a write that landed on a screen that could not be
 * re-read. It is held from then on, because there is no correct next operation to allow.
 */
const BUSY_ATTRIBUTE = 'data-rankings-busy';
const STALE_ATTRIBUTE = 'data-rankings-stale';

export function isRankingBusy(root) {
    return editRootOf(root)?.hasAttribute(BUSY_ATTRIBUTE) === true;
}

export function setRankingBusy(root, busy) {
    const editRoot = editRootOf(root);
    if (editRoot === null) {
        return;
    }

    if (busy) {
        editRoot.setAttribute(BUSY_ATTRIBUTE, '');

        return;
    }
    // Releasing a stale screen is the one thing the lock must refuse. Every control here addresses a slot
    // by its position, so the release has to wait for markup that agrees with the database - and there is
    // none coming.
    if (!isRankingStale(root)) {
        editRoot.removeAttribute(BUSY_ATTRIBUTE);
    }
}

/**
 * A write that landed while the screen could not be brought back in line with it. Terminal, and
 * deliberately so: the positions on screen are the ones from before the operation, so the next submit
 * would address a slot by a number that no longer means what the server means by it. `Add` into what still
 * looks like an empty `#2` is a real overwrite of the game the previous operation just stored there -
 * `RankingCrudService::assign()` replaces an occupied slot, and it is right to, because nothing reached it
 * saying otherwise.
 *
 * Only a reload clears it, which is what the message on screen asks for. The write itself succeeded, so
 * reloading loses nothing.
 */
export function markRankingStale(root) {
    const editRoot = editRootOf(root);
    if (editRoot === null) {
        return;
    }

    editRoot.setAttribute(STALE_ATTRIBUTE, '');
    editRoot.setAttribute(BUSY_ATTRIBUTE, '');
    // Refusing the request is not enough: a control that keeps its place in the tab order and answers a
    // keypress with nothing is worse than one that says it is out of order. Every button in here writes by
    // position, so every button shuts - and shuts properly, which is what takes it out of tab order and
    // announces it.
    //
    // The links are deliberately left alone. Each one is an ordinary navigation to a canonical URL - the
    // game, `Edit dates`, the next window of matches, the position link that the script was intercepting -
    // and a fresh render is precisely what makes this screen current again. `inert` here would take the
    // ranking itself away from a screen reader and close the way out along with it.
    editRoot.querySelectorAll('button').forEach((control) => {
        control.disabled = true;
    });
}

export function isRankingStale(root) {
    return editRootOf(root)?.hasAttribute(STALE_ATTRIBUTE) === true;
}

/**
 * The lock lives on the editing container, which is the scope of every ranking write. Without one there is
 * no editing mode on the page and nothing to serialise.
 */
function editRootOf(root) {
    const editRoot = root?.querySelector?.('[data-rankings-edit]');

    return editRoot instanceof HTMLElement ? editRoot : null;
}
